/**
 * Maintenance Charts - Activity visualizations
 */
import { useMemo } from 'react';
import { Paper, Text, Stack, Center, Grid, Group, SegmentedControl } from '@mantine/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';
import type { MaintenanceActivity } from '@/types/maintenance';
import { getTaskTypeLabel, getTaskTypeColor } from '@/types/maintenance';
import { generateCalendarDates } from '@/utils/calendarDates';

/**
 * Security Issue #7: Sanitize user-controlled content to prevent XSS
 * Simple text sanitization - removes HTML tags and dangerous characters
 */
function sanitizeText(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

interface MaintenanceChartsProps {
  activities: MaintenanceActivity[];
  timelineView?: 'activities' | 'equipment';
  onTimelineViewChange?: (view: 'activities' | 'equipment') => void;
}

export function MaintenanceCharts({
  activities,
  timelineView = 'activities',
  onTimelineViewChange,
}: MaintenanceChartsProps) {
  // Performance Issue #4: Optimize by combining calculations in single pass
  const dailyChartData = useMemo(() => {
    if (activities.length === 0) {
      return [];
    }

    // Single pass: collect dates, group activities, and track task types
    const dates: string[] = [];
    const dailyData: Record<
      string,
      { date: string; [taskType: string]: number | string }
    > = {};
    const taskTypeSet = new Set<string>();

    activities.forEach((activity) => {
      const dateKey = activity.activity_date;
      const taskTypeLabel = getTaskTypeLabel(activity.task_type);

      // Track date
      dates.push(dateKey);

      // Track task type
      taskTypeSet.add(taskTypeLabel);

      // Group by date and task type
      if (!dailyData[dateKey]) {
        dailyData[dateKey] = { date: dateKey };
      }

      const dayData = dailyData[dateKey]!;
      if (typeof dayData[taskTypeLabel] === 'number') {
        dayData[taskTypeLabel] = (dayData[taskTypeLabel] as number) + 1;
      } else {
        dayData[taskTypeLabel] = 1;
      }
    });

    // Calculate date range (single sort instead of multiple iterations)
    const sortedDates = dates.sort();
    const range = {
      start: sortedDates[0]!,
      end: sortedDates[sortedDates.length - 1]!,
    };

    // Generate complete calendar date range
    const calendarDates = generateCalendarDates(range.start!, range.end!);
    const taskTypes = Array.from(taskTypeSet);

    // Merge calendar dates with activity data (fill zeros)
    const completeData = calendarDates.map((date) => {
      const existingData = dailyData[date] || { date };

      // Ensure all task types exist with 0 as default
      const completeDay: Record<string, number | string> = { date };
      taskTypes.forEach((taskType) => {
        completeDay[taskType] =
          typeof existingData[taskType] === 'number' ? existingData[taskType] : 0;
      });

      return completeDay;
    });

    return completeData;
  }, [activities]);

  // Get unique task types for bars
  const taskTypes = useMemo(() => {
    const uniqueTypes = new Set<number>();
    activities.forEach((activity) => {
      uniqueTypes.add(activity.task_type);
    });

    return Array.from(uniqueTypes)
      .sort((a, b) => a - b)
      .map((type) => ({
        value: type,
        label: getTaskTypeLabel(type),
        color: getTaskTypeColor(type),
      }));
  }, [activities]);

  // Process activities for scatter chart
  const scatterChartData = useMemo(() => {
    return activities.map((activity) => ({
      date: new Date(activity.activity_date).getTime(),
      taskType: activity.task_type,
      taskTypeLabel: getTaskTypeLabel(activity.task_type),
      taskTitle: activity.task_title,
      equipmentName: activity.equipment_name || undefined,
      performed: activity.performed ? 1 : 0,
    }));
  }, [activities]);

  // Process equipment-based timeline data
  // Performance Issue #5: Memoize equipment count for height calculation
  const { equipmentChartData, uniqueEquipmentCount } = useMemo(() => {
    // Filter to only activities with equipment
    const withEquipment = activities.filter((a) => a.equipment_name);

    if (withEquipment.length === 0) {
      return { equipmentChartData: [], uniqueEquipmentCount: 0 };
    }

    // Group by date, equipment, AND task type to preserve all task types
    const grouped: Record<
      string,
      {
        tasks: string[];
        taskType: number;
        date: Date;
        equipment: string;
      }
    > = {};
    const equipmentSet = new Set<string>();

    withEquipment.forEach((activity) => {
      const dateKey = activity.activity_date;
      const equipment = activity.equipment_name!;

      // Track unique equipment for height calculation
      equipmentSet.add(equipment);

      // Include task type in grouping key to create separate points per task type
      const groupKey = `${dateKey}-${equipment}-${activity.task_type}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          tasks: [],
          taskType: activity.task_type,
          date: new Date(activity.activity_date),
          equipment,
        };
      }

      // Collect unique tasks for this equipment/date/type combination
      if (!grouped[groupKey].tasks.includes(activity.task_title)) {
        grouped[groupKey].tasks.push(activity.task_title);
      }
    });

    // Flatten to array for charting
    const result: Array<{
      date: number;
      equipment: string;
      tasks: string;
      taskTypeLabel: string;
      taskType: number;
    }> = [];

    Object.values(grouped).forEach((data) => {
      result.push({
        date: data.date.getTime(),
        equipment: data.equipment,
        tasks: data.tasks.join(', '),
        taskTypeLabel: getTaskTypeLabel(data.taskType),
        taskType: data.taskType,
      });
    });

    return {
      equipmentChartData: result,
      uniqueEquipmentCount: equipmentSet.size
    };
  }, [activities]);

  // Format date for display
  const formatDate = (dateString: string | number) => {
    const date = typeof dateString === 'number' ? new Date(dateString) : new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (activities.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Center py="xl">
          <Text c="dimmed">No maintenance activities to display</Text>
        </Center>
      </Paper>
    );
  }

  return (
    <Grid gutter="md">
      {/* Daily Activity Counts */}
      <Grid.Col span={12}>
        <Paper p="md" withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Daily Activity Counts by Type
            </Text>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  label={{ value: 'Activity Count', angle: -90, position: 'insideLeft' }}
                  allowDecimals={false}
                />
                <Tooltip
                  labelFormatter={formatDate}
                  formatter={(value: number) => `${value} activities`}
                />
                <Legend />
                {taskTypes.map((taskType) => (
                  <Bar
                    key={taskType.value}
                    dataKey={taskType.label}
                    stackId="a"
                    fill={taskType.color}
                    name={taskType.label}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Stack>
        </Paper>
      </Grid.Col>

      {/* Timeline View Toggle */}
      <Grid.Col span={12}>
        <Paper p="md" withBorder>
          <Group justify="space-between" align="center">
            <Text size="lg" fw={600}>
              Timeline View
            </Text>
            <SegmentedControl
              value={timelineView}
              onChange={(value) =>
                onTimelineViewChange?.(value as 'activities' | 'equipment')
              }
              data={[
                { label: 'Activities', value: 'activities' },
                { label: 'Equipment', value: 'equipment' },
              ]}
            />
          </Group>
        </Paper>
      </Grid.Col>

      {/* Conditional Timeline Chart */}
      {timelineView === 'activities' ? (
        // Activity Timeline
        <Grid.Col span={12}>
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Text size="lg" fw={600}>
                Individual Activities Timeline
              </Text>
              <ResponsiveContainer width="100%" height={Math.max(400, taskTypes.length * 50)}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    type="number"
                    domain={['auto', 'auto']}
                    tickFormatter={formatDate}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    dataKey="taskTitle"
                    type="category"
                    width={200}
                    tick={{ fontSize: 12 }}
                  />
                  <ZAxis range={[60, 60]} />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;
                      const data = payload[0]?.payload;
                      if (!data) return null;

                      return (
                        <Paper p="xs" withBorder>
                          <Stack gap={4}>
                            <Text size="sm" fw={600}>
                              {formatDate(data.date)}
                            </Text>
                            <Text size="sm" c="dimmed">
                              Activity: {sanitizeText(data.taskTitle)}
                            </Text>
                            <Text size="sm" c="dimmed">
                              Type: {sanitizeText(data.taskTypeLabel)}
                            </Text>
                            {data.equipmentName && (
                              <Text size="sm" c="dimmed">
                                Equipment: {sanitizeText(data.equipmentName)}
                              </Text>
                            )}
                            <Text
                              size="sm"
                              c={data.performed === 1 ? 'green' : 'orange'}
                              fw={500}
                            >
                              {data.performed === 1 ? 'Performed' : 'Pending'}
                            </Text>
                          </Stack>
                        </Paper>
                      );
                    }}
                  />
                  <Legend />
                  {taskTypes.map((taskType) => (
                    <Scatter
                      key={taskType.value}
                      name={taskType.label}
                      data={scatterChartData.filter((d) => d.taskType === taskType.value)}
                      fill={taskType.color}
                    />
                  ))}
                </ScatterChart>
              </ResponsiveContainer>
            </Stack>
          </Paper>
        </Grid.Col>
      ) : (
        // Equipment Timeline
        <Grid.Col span={12}>
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Text size="lg" fw={600}>
                Equipment Timeline
              </Text>
              {equipmentChartData.length > 0 ? (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(300, uniqueEquipmentCount * 40)}
                >
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      type="number"
                      domain={['auto', 'auto']}
                      tickFormatter={formatDate}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      dataKey="equipment"
                      type="category"
                      width={150}
                      tick={{ fontSize: 12 }}
                    />
                    <ZAxis range={[120, 120]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ payload }) => {
                        if (!payload || payload.length === 0) return null;
                        const data = payload[0]?.payload;
                        if (!data) return null;

                        return (
                          <Paper p="xs" withBorder>
                            <Stack gap={4}>
                              <Text size="sm" fw={600}>
                                {formatDate(data.date)}
                              </Text>
                              <Text size="sm" c="dimmed">
                                Equipment: {sanitizeText(data.equipment)}
                              </Text>
                              <Text size="sm" c="dimmed">
                                Activities: {sanitizeText(data.tasks)}
                              </Text>
                              <Text size="sm" c="dimmed">
                                Type: {sanitizeText(data.taskTypeLabel)}
                              </Text>
                            </Stack>
                          </Paper>
                        );
                      }}
                    />
                    <Legend />
                    {taskTypes.map((taskType) => (
                      <Scatter
                        key={taskType.value}
                        name={taskType.label}
                        data={equipmentChartData.filter((d) => d.taskType === taskType.value)}
                        fill={taskType.color}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <Center py="xl">
                  <Text c="dimmed">No equipment-based activities found in selected filters.</Text>
                </Center>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
      )}
    </Grid>
  );
}
